import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { getDisplayName } from '../../../../models/NameUtils';
import { useAdminAccountUsersQuery } from '../../../../queries/AdminTools/useAdminAccountUsersQuery';
import { useSupportUserSearchQuery } from '../../../../queries/Support/useSupportUserSearch';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { supportSharedStyles as shared } from '../styles/supportSharedStyles';
import type { NewTicketDraftAccount, NewTicketDraftContact } from './newTicketDraft';
import { newTicketStyles as s } from './styles';

interface ContactOption extends NewTicketDraftContact {
	account?: NewTicketDraftAccount;
}

interface ContactFieldProps {
	account: NewTicketDraftAccount | null;
	contact: NewTicketDraftContact | null;
	manualContact: boolean;
	manualEmailError?: string;
	/** A picked contact can carry its own account (global search); the modal adopts it. */
	onChangeContact: (contact: NewTicketDraftContact | null, contactAccount?: NewTicketDraftAccount) => void;
	onToggleManual: (manual: boolean) => void;
}

const CONTACT_RESULTS_PAGE_SIZE = 8;
// Below this many users the whole account fits in the list, so the search box is just noise.
const CONTACT_SEARCH_MIN_USERS = 5;

/**
 * Contact picker for the new-ticket form. With an account selected it searches that account's
 * users (same endpoint as the account details Users tab); without one it falls back to the
 * support desk's global user search. Manual entry covers contacts that don't exist as users.
 */
export const ContactField: React.FC<ContactFieldProps> = ({
	account,
	contact,
	manualContact,
	manualEmailError,
	onChangeContact,
	onToggleManual,
}) => {
	const [search, setSearch] = React.useState('');
	const debouncedSearch = useDebounceValue(search.trim(), 300);

	// The paginated endpoint is 0-based (page 0 is the first page).
	const accountUsersQuery = useAdminAccountUsersQuery({
		accountId: account?.id ?? '',
		page: 0,
		pageSize: CONTACT_RESULTS_PAGE_SIZE,
		statuses: [],
		roles: [],
		search: debouncedSearch,
	});
	const globalSearchQuery = useSupportUserSearchQuery(account ? '' : debouncedSearch);

	// The query keeps previous data as a placeholder across key changes, which is pleasant while
	// refining a search but dangerous across account switches: the old account's users would render
	// as clickable options. Dropping rows from other accounts closes that hole; same-account
	// placeholders (search refinement) pass through untouched.
	const options: ContactOption[] = account
		? (accountUsersQuery.data?.values ?? [])
				.filter(user => user.accountId === account.id)
				.map(user => ({
					userId: user.id,
					email: user.email,
					name: getDisplayName(user),
				}))
		: (globalSearchQuery.data ?? []).map(user => ({
				userId: user.userId,
				email: user.email,
				name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
				account: { id: user.accountId, name: user.accountName },
			}));
	const isSearching = account
		? accountUsersQuery.isLoading || (accountUsersQuery.isFetching && options.length === 0)
		: !!debouncedSearch && globalSearchQuery.isLoading;
	const showEmptyResult = account
		? !accountUsersQuery.isLoading && !accountUsersQuery.isFetching && options.length === 0
		: !!debouncedSearch && !globalSearchQuery.isLoading && options.length === 0;
	// With no search active the count is the account's full user count; a handful of users all fit
	// in the list, so skip the search box. Hidden means search stays empty, keeping this stable.
	// A placeholder count belongs to the previous query, so it must not drive the decision.
	const accountUserTotal = accountUsersQuery.isPlaceholderData ? undefined : accountUsersQuery.data?.totalCount;
	const showSearchInput = account
		? search !== '' || (accountUserTotal !== undefined && accountUserTotal >= CONTACT_SEARCH_MIN_USERS)
		: true;

	if (manualContact) {
		return (
			<>
				<div>
					<Label styles={[shared.modalLabel]}>Contact email</Label>
					<Input
						styles={[shared.modalInput, manualEmailError && shared.modalInputError]}
						type='email'
						placeholder='customer@example.com'
						value={contact?.email ?? ''}
						onChange={e => onChangeContact({ ...contact, userId: undefined, email: e.target.value })}
					/>
					{manualEmailError && <div className={css(shared.modalErrorText)}>{manualEmailError}</div>}
				</div>
				<div>
					<Label styles={[shared.modalLabel]}>Contact name (optional)</Label>
					<Input
						styles={[shared.modalInput]}
						type='text'
						placeholder='John Doe'
						value={contact?.name ?? ''}
						onChange={e => onChangeContact({ ...contact, userId: undefined, name: e.target.value })}
					/>
				</div>
				<Button variant='link' styles={[s.manualLink]} onClick={() => onToggleManual(false)}>
					Back to contact search
				</Button>
			</>
		);
	}

	if (contact) {
		return (
			<div className={css(s.selectedContact)}>
				<div className={css(s.contactResultIdentity)}>
					<span className={css(s.contactResultName)}>{contact.name || contact.email}</span>
					{contact.name && <span className={css(s.contactResultEmail)}>{contact.email}</span>}
				</div>
				<Button variant='ghost' size='sm' onClick={() => onChangeContact(null)}>
					Change
				</Button>
			</div>
		);
	}

	return (
		<>
			{showSearchInput && (
				<Input
					styles={[shared.modalInput]}
					type='text'
					placeholder={
						account ? `Search contacts at ${account.name || 'this account'}...` : 'Search by name or email...'
					}
					value={search}
					onChange={e => setSearch(e.target.value)}
				/>
			)}
			{isSearching && (
				<div className={css(shared.modalSpinnerRow)}>
					<LoadingSpinner type='small' />
				</div>
			)}
			{options.length > 0 && (
				<div className={css(s.contactResults)}>
					{options.map(option => (
						<Button
							key={option.userId}
							type='button'
							variant='ghost'
							styles={[s.contactResult]}
							onClick={() =>
								onChangeContact({ userId: option.userId, email: option.email, name: option.name }, option.account)
							}
						>
							<span className={css(s.contactResultIdentity)}>
								<span className={css(s.contactResultName)}>{option.name || option.email}</span>
								{option.name && <span className={css(s.contactResultEmail)}>{option.email}</span>}
							</span>
							{option.account?.name && <span className={css(s.contactResultAccount)}>{option.account.name}</span>}
						</Button>
					))}
				</div>
			)}
			{showEmptyResult && <div className={css(s.csmRow)}>No contacts found</div>}
			<Button variant='link' styles={[s.manualLink]} onClick={() => onToggleManual(true)}>
				Enter contact manually
			</Button>
		</>
	);
};
